/**
 * PageTransitionContext
 * Provides a `navigateWithFade(path)` function that fades the screen
 * to white before navigating, then fades back in on arrival.
 *
 * Timings (intentionally faster than the auth handoff):
 *   Fade-out  → 240 ms  cubic-bezier(0.4, 0, 1, 1)    – quick, decisive
 *   Fade-in   → 360 ms  cubic-bezier(0.0, 0, 0.2, 1)   – calm reveal
 *
 * The veil is rendered via a portal to document.body so it is never
 * trapped inside a transform / filter stacking context.
 */
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
    clearAllAiTasks,
    clearExpiredAiTasks,
    selectBlockingAiTasks,
    selectHasBlockingAiTask,
} from '../Reducers/aiTasksReducer';

const PageTransitionContext = createContext(null);

export function PageTransitionProvider({ children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation('common');
    const dispatch = useDispatch();
    const activeScopeKey = useSelector((state) => {
        if (!location.pathname.startsWith('/stage-review')) return '';
        return [
            state.stage?.id,
            state.documentVersion?.currentVersion,
            state.documentVersion?.content?.id,
        ].filter(Boolean).join('\u001f');
    });
    const activeScopeIds = useMemo(
        () => activeScopeKey ? activeScopeKey.split('\u001f') : [],
        [activeScopeKey]
    );
    const selectorOptions = useMemo(
        () => ({ activeScopeIds }),
        [activeScopeIds]
    );
    const hasBlockingAiTask = useSelector((state) => selectHasBlockingAiTask(state, selectorOptions));
    const blockingAiTasks = useSelector((state) => selectBlockingAiTasks(state, selectorOptions));

    const [veilMounted, setVeilMounted] = useState(false);
    const [veilActive, setVeilActive] = useState(false);

    const timerFade   = useRef(null);
    const timerUnmount = useRef(null);
    const pendingPath  = useRef(null);
    const prevPath     = useRef(location.pathname);
    const rafHandles   = useRef({ r1: 0, r2: 0 });
    const guardStateRef = useRef({ hasBlockingAiTask: false, blockingAiTasks: [] });

    useEffect(() => {
        guardStateRef.current = { hasBlockingAiTask, blockingAiTasks };
    }, [blockingAiTasks, hasBlockingAiTask]);

    const clearTimers = useCallback(() => {
        clearTimeout(timerFade.current);
        clearTimeout(timerUnmount.current);
        cancelAnimationFrame(rafHandles.current.r1);
        cancelAnimationFrame(rafHandles.current.r2);
    }, []);

    useEffect(() => () => clearTimers(), [clearTimers]);

    useEffect(() => {
        dispatch(clearExpiredAiTasks());
    }, [dispatch]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (!guardStateRef.current.hasBlockingAiTask) return undefined;
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    const confirmAiNavigationIfNeeded = useCallback(() => {
        const { hasBlockingAiTask: shouldBlock, blockingAiTasks: tasks } = guardStateRef.current;
        if (!shouldBlock) return Promise.resolve(true);

        const firstTask = tasks[0];
        const taskLabel = firstTask?.label || t('navigationGuard.aiTaskFallback', {
            defaultValue: 'un traitement IA',
        });
        const taskCount = tasks.length;
        const content = taskCount > 1
            ? t('navigationGuard.contentMany', {
                count: taskCount,
                defaultValue: '{{count}} traitements IA sont en cours. Quitter cette page peut interrompre leur affichage.',
            })
            : t('navigationGuard.contentOne', {
                taskLabel,
                defaultValue: '{{taskLabel}} est en cours. Quitter cette page peut interrompre son affichage.',
            });

        return new Promise((resolve) => {
            Modal.confirm({
                title: t('navigationGuard.title', { defaultValue: 'Traitement IA en cours' }),
                content,
                okText: t('navigationGuard.leave', { defaultValue: 'Quitter quand même' }),
                cancelText: t('navigationGuard.stay', { defaultValue: 'Rester sur la page' }),
                okButtonProps: { danger: true },
                centered: true,
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
            });
        });
    }, [t]);

    // ── Fade-out → navigate ───────────────────────────────────────────────────
    const navigateWithFade = useCallback(async (path) => {
        if (path === location.pathname) return;
        const confirmed = await confirmAiNavigationIfNeeded();
        if (!confirmed) return;

        if (path === '/workspace' || path === '/') {
            dispatch(clearAllAiTasks());
        }

        clearTimers();
        pendingPath.current = path;

        setVeilMounted(true);

        // Two RAFs so the browser paints the transparent veil first,
        // then the CSS transition to opacity:1 runs properly.
        rafHandles.current.r1 = requestAnimationFrame(() => {
            rafHandles.current.r2 = requestAnimationFrame(() => {
                setVeilActive(true);
                // Navigate slightly after the fade has finished (240 ms CSS duration)
                timerFade.current = setTimeout(() => {
                    navigate(path);
                }, 260);
            });
        });
    }, [location.pathname, confirmAiNavigationIfNeeded, dispatch, navigate, clearTimers]);

    // ── Fade-in on route change ───────────────────────────────────────────────
    useEffect(() => {
        if (prevPath.current === location.pathname) return;
        prevPath.current = location.pathname;

        // The veil is already opaque (set by navigateWithFade before nav).
        // Kick off the reverse dissolve.
        clearTimers();
        rafHandles.current.r1 = requestAnimationFrame(() => {
            rafHandles.current.r2 = requestAnimationFrame(() => {
                setVeilActive(false);
                // Unmount cleanly after the CSS transition completes (360 ms)
                timerUnmount.current = setTimeout(() => setVeilMounted(false), 420);
            });
        });
    }, [location, clearTimers]);

    return (
        <PageTransitionContext.Provider value={{ navigateWithFade, confirmAiNavigationIfNeeded }}>
            {veilMounted && createPortal(
                <div
                    className={`page-fade-veil${veilActive ? ' is-active' : ''}`}
                    aria-hidden="true"
                />,
                document.body
            )}
            {children}
        </PageTransitionContext.Provider>
    );
}

export function usePageTransition() {
    const ctx = useContext(PageTransitionContext);
    // Graceful fallback if used outside provider (e.g. tests)
    if (!ctx) return { navigateWithFade: () => {}, confirmAiNavigationIfNeeded: () => Promise.resolve(true) };
    return ctx;
}

